/**
 * Local pre-check for a multi-file / whole-folder selection
 * (VistoeraTASK-00522). Pure and framework-agnostic: it only reads
 * `File` METADATA (name / size / type / webkitRelativePath) - it never reads
 * file contents, decodes images or builds thumbnails, so scanning 500 files
 * stays instant and cannot spike browser memory.
 */
import { EXTENSION_TO_MIME, KNOWN_UNSUPPORTED_IMAGE_EXTENSIONS, type UploadLimits } from './uploadLimits'

export interface FileLike {
  name: string
  size: number
  type?: string
  webkitRelativePath?: string
}

export type SkipReason = 'not_image' | 'unsupported_type' | 'oversize' | 'empty' | 'hidden_system'

export interface ValidFile<F extends FileLike> {
  file: F
  mime: string
  /** Path inside the selected folder ("Folder/sub/a.jpg"), or just the name. */
  relativePath: string
}

export interface SkippedFile {
  name: string
  relativePath: string
  size: number
  reason: SkipReason
}

export type LimitErrorCode = 'too_many_files' | 'too_large_total' | 'nothing_to_upload'

export interface LimitError {
  code: LimitErrorCode
  /** too_many_files: selected count; too_large_total: selected bytes. */
  actual: number
  /** The limit that was exceeded (files / bytes). */
  max: number
}

export interface ScanResult<F extends FileLike> {
  folderName: string | null
  scannedCount: number
  valid: Array<ValidFile<F>>
  skipped: SkippedFile[]
  skippedByReason: Record<SkipReason, number>
  /** Sum of `size` over `valid` only. */
  totalBytes: number
  limitErrors: LimitError[]
  /** true => nothing may be started (any limit error). */
  blocked: boolean
}

const HIDDEN_SYSTEM_NAMES = new Set(['thumbs.db', 'desktop.ini', '.ds_store'])

function extensionOf(name: string): string {
  const i = name.lastIndexOf('.')
  return i > 0 ? name.slice(i + 1).toLowerCase() : ''
}

function baseName(path: string): string {
  const parts = path.split('/')
  return parts[parts.length - 1] || path
}

function isHiddenOrSystem(relativePath: string): boolean {
  const segments = relativePath.split('/').filter(Boolean)
  if (segments.some((s) => s === '__MACOSX')) return true
  const name = segments[segments.length - 1] || ''
  // `.hidden`, macOS AppleDouble `._x.jpg`, Thumbs.db, desktop.ini, .DS_Store
  return name.startsWith('.') || HIDDEN_SYSTEM_NAMES.has(name.toLowerCase())
}

/**
 * Resolve the MIME to upload with, or the reason the file is skipped.
 * `File.type` wins when it is a supported image type; an empty / generic
 * type falls back to the extension (some browsers/OSes give none for folder
 * files). A non-empty type that is a DIFFERENT, unsupported image type is
 * never overridden by its extension.
 */
export function resolveMime(
  file: FileLike,
  allowedMime: ReadonlySet<string>,
): { mime: string } | { reason: SkipReason } {
  const type = (file.type || '').toLowerCase().trim()
  const ext = extensionOf(file.name)
  if (type && allowedMime.has(type)) return { mime: type }
  const typeIsGeneric = !type || type === 'application/octet-stream'
  if (typeIsGeneric) {
    const inferred = EXTENSION_TO_MIME[ext]
    if (inferred && allowedMime.has(inferred)) return { mime: inferred }
    if (KNOWN_UNSUPPORTED_IMAGE_EXTENSIONS.has(ext)) return { reason: 'unsupported_type' }
    return { reason: 'not_image' }
  }
  if (type.startsWith('image/')) return { reason: 'unsupported_type' }
  return { reason: 'not_image' }
}

export function scanFiles<F extends FileLike>(files: Iterable<F>, limits: UploadLimits): ScanResult<F> {
  const valid: Array<ValidFile<F>> = []
  const skipped: SkippedFile[] = []
  const skippedByReason: Record<SkipReason, number> = {
    not_image: 0,
    unsupported_type: 0,
    oversize: 0,
    empty: 0,
    hidden_system: 0,
  }
  let scanned = 0
  let totalBytes = 0
  let folderName: string | null = null

  const skip = (file: F, relativePath: string, reason: SkipReason) => {
    skipped.push({ name: file.name, relativePath, size: file.size, reason })
    skippedByReason[reason] += 1
  }

  for (const file of files) {
    scanned += 1
    const relativePath = (file.webkitRelativePath || '').trim() || file.name
    if (folderName === null && file.webkitRelativePath) {
      const first = file.webkitRelativePath.split('/')[0]
      if (first) folderName = first
    }
    if (isHiddenOrSystem(relativePath)) {
      skip(file, relativePath, 'hidden_system')
      continue
    }
    const resolved = resolveMime(file, limits.allowedMime)
    if ('reason' in resolved) {
      skip(file, relativePath, resolved.reason)
      continue
    }
    if (!(file.size > 0)) {
      skip(file, relativePath, 'empty')
      continue
    }
    if (file.size > limits.maxFileBytes) {
      skip(file, relativePath, 'oversize')
      continue
    }
    valid.push({ file, mime: resolved.mime, relativePath })
    totalBytes += file.size
  }

  const limitErrors: LimitError[] = []
  if (valid.length === 0) limitErrors.push({ code: 'nothing_to_upload', actual: 0, max: 0 })
  // Never silently truncate to the first N - the whole selection is refused.
  if (valid.length > limits.maxFiles) {
    limitErrors.push({ code: 'too_many_files', actual: valid.length, max: limits.maxFiles })
  }
  if (totalBytes > limits.maxTotalBytes) {
    limitErrors.push({ code: 'too_large_total', actual: totalBytes, max: limits.maxTotalBytes })
  }

  return {
    folderName,
    scannedCount: scanned,
    valid,
    skipped,
    skippedByReason,
    totalBytes,
    limitErrors,
    blocked: limitErrors.length > 0,
  }
}

export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

/** Folder picking relies on the non-standard-but-universal `webkitdirectory`. */
export function supportsDirectoryPicker(doc: { createElement: (tag: string) => unknown } | undefined = typeof document !== 'undefined' ? document : undefined): boolean {
  if (!doc) return false
  const input = doc.createElement('input') as { webkitdirectory?: unknown }
  return typeof input === 'object' && input !== null && 'webkitdirectory' in input
}

export { baseName }
